import { Chip } from "@mui/material"
import type { ColumnDef } from "@tanstack/react-table"

import { FileRowActions } from "../components/FileRowActions"

import FileChip from "@/components/upload/components/display/FileChip"
import type { FieldGroupEnum, SignedUrl } from "@/graphql/codegen/graphql"
import colors from "@/styles/colors"

export interface FileTypeChip {
  label: string
  color: string
}

export type FileTypeToChipMap = Partial<Record<FieldGroupEnum, FileTypeChip>>

const OTHER_FILE_TYPE_CHIP: FileTypeChip = { label: "Other", color: colors.tertiary.purple["100"] }

interface UseFileColumnsProps {
  // Omit to hide the type column entirely; pass a map, even {}, to show it.
  typeToChip?: FileTypeToChipMap
  canDownload?: boolean
  canDelete?: boolean
  onDelete?: (file: SignedUrl) => void
}

export const useFileColumns = ({
  typeToChip,
  canDownload,
  canDelete,
  onDelete,
}: UseFileColumnsProps): ColumnDef<SignedUrl>[] => {
  const typeColumn: ColumnDef<SignedUrl> | null = typeToChip
    ? {
        accessorKey: "type",
        header: "Type",
        enableSorting: false,
        cell: ({ row }) => {
          const fieldGroup = row.original.file?.fieldGroup
          const chip = (fieldGroup && typeToChip[fieldGroup]) || OTHER_FILE_TYPE_CHIP

          return <Chip label={chip.label} variant='filled' size='small' sx={{ backgroundColor: chip.color }} />
        },
      }
    : null

  return [
    ...(typeColumn ? [typeColumn] : []),
    {
      accessorKey: "originalName",
      header: "File",
      enableSorting: false,
      cell: ({ row }) => <FileChip file={row.original} />,
    },
    {
      accessorKey: "description",
      header: "Description",
      enableSorting: false,
      cell: ({ row }) => row.original.file?.label ?? null,
    },
    {
      accessorKey: "actions",
      header: "",
      enableSorting: false,
      size: 72,
      cell: ({ row }) => (
        <FileRowActions row={row} canDownload={canDownload} canDelete={canDelete} onDelete={onDelete} />
      ),
    },
  ]
}
